import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import {
    LoadingIndicator
} from "../../common/VFX-UI";

//App
import Settings from "./Settings";
import settingApi from "../../../app/model/api/settingApi";
import { queryKeys } from "./constants";
import { GRID_PAGE_SIZE } from "../../../utility/constants/constants";
import { SettingContextData, SettingUseState } from "../../../types/setting";
import { selectIsActive, setIsActive, selectSearchValue, setSearchValue } from "../../../store/setting";
import { SearchValue } from "../../../store/types";
import { SortBy } from "../../../types";


export const SettingContext = React.createContext<SettingContextData | null>(
    null
);

const SettingsContainer = () => {
    const dispatch = useDispatch();

    const [state, setState] = useState<SettingUseState>({
        settingId: null,
        activeToggle: useSelector(selectIsActive) ?? true,
        searchPayload: useSelector(selectSearchValue) ?? null,
        currentPage: 1,
        pageSize: GRID_PAGE_SIZE,
        sortBy: [{ field: "name", direction: 1 }],
    });


    const settingGridQuery = useQuery({
        queryKey: [queryKeys.getSettings],
        queryFn: () => settingApi.searchSettings({
            ...state.searchPayload,
            isActive: state.activeToggle,
            pageSize: state.pageSize,
            pageNumber: state.currentPage,
            sortBy: state.sortBy, }),
    });

    const { mutate: deleteMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await settingApi.delete(value);
        },
        onError: () => console.log("error"),
        onSuccess: () => console.log("success"),
    });

    const { mutate: activateMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await settingApi.activate(value);
        },
        onError: () => console.log("error"),
        onSuccess: () => console.log("success"),
    });

    const updateSettingId = async (settingId: string | null) => {
        setState((prevState) => ({
            ...prevState,
            settingId: settingId,
        }));
    };

    const updateActiveToggle = async () => {
        dispatch(setIsActive());
        setState((prevState) => ({
            ...prevState,
            activeToggle: !prevState.activeToggle,
        }));
    };

    const updateSearchPayload = async (values: SearchValue | null) => {
        if (values?.name) {
            dispatch(setSearchValue({ name: values.name }));
        }
        if (values && Object.keys(values).length) {
            setState((prevState) => ({
                ...prevState,
                searchPayload: values,
                currentPage: 1,
            }));
        } else {
            setState((prevState) => ({
                ...prevState,
                searchPayload: null,
                currentPage: 1,
                sortBy: [{ field: "name", direction: 1 }],
            }));
        }
    };

    const updateCurrentPage = async (currentPage: number) => {
        setState((prevState) => ({
            ...prevState,
            currentPage: currentPage,
        }));
    };

    const updateCurrentPageSize = async (pageSize: number) => {
        setState((prevState) => ({
          ...prevState,
          pageSize: pageSize,
        }));
      };  

    const updateSortBy = async (sortBy: SortBy) => {
        const { field, direction } = sortBy;
        setState((prevState) => ({
            ...prevState,
            sortBy: [{ field, direction }],
        }));
    };

    const contextValue: SettingContextData = {
        data: settingGridQuery?.data?.result.find((x: any) => x.id === state.settingId),
        id: state.settingId,
        activeToggle: state.activeToggle,
        setId: updateSettingId,
        refetchGrid: () => settingGridQuery.refetch(),
        setActiveToggleValue: updateActiveToggle,
        setSearchValue: updateSearchPayload,
        setCurrentPage: updateCurrentPage,
        setCurrentPageSize: updateCurrentPageSize,
        setSortBy: updateSortBy,
        paging: {
            currentPage: state.currentPage,
            totalSize: settingGridQuery.data?.totalSize ?? 0,
        },
        pageSize: state.pageSize,
    };

    return settingGridQuery.isLoading ? (
        <LoadingIndicator />
    ) : (
        <SettingContext.Provider value={contextValue}>
            <Settings
                data={settingGridQuery?.data?.result}
                deleteMutation={deleteMutation}
                activateMutation={activateMutation}
            />
        </SettingContext.Provider>
    );
};

export default SettingsContainer;
